/**
 * The request path with its query string, built by hand because `apiClient.get` takes a path only
 * (F26-b contract client-side fact 2; F26-b WI-1.3 Decision 14).
 *
 * **Only the parameters the caller lists are appended, in the order listed** — every endpoint on the
 * seam answers an undeclared parameter with `400 VALIDATION_FAILED`, never ignoring it, so each
 * caller lists exactly what its contract row declares. A value that is `undefined`, `null` or blank
 * (after trimming) is **omitted rather than sent empty**: `q=` is a refusal, not a no-op. Every value
 * goes through `encodeURIComponent`, so a search carrying a space, `&` or `+` arrives as typed.
 *
 * Promoted from `src/features/email/inboxApi.ts`'s `buildMessagesPath` because four modules now need
 * it; the email feature keeps its own.
 */

/** One query-string value. A list repeats the parameter (`accountId=1&accountId=2`). */
export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

/** The parameters in the order they are sent. */
export type QueryParams = readonly (readonly [string, QueryValue])[];

function present(value: string | number | boolean): string | null {
    const text = typeof value === 'string' ? value.trim() : String(value);
    return text === '' ? null : text;
}

export function pathWithQuery(path: string, params: QueryParams): string {
    const pairs: string[] = [];
    for (const [key, value] of params) {
        if (value === undefined || value === null) {
            continue;
        }
        const values = Array.isArray(value) ? value : [value];
        for (const one of values) {
            const text = present(one);
            if (text !== null) {
                pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(text)}`);
            }
        }
    }
    return pairs.length === 0 ? path : `${path}?${pairs.join('&')}`;
}
